import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Collapse, Typography } from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles(theme => ({
    wrapper: {
        marginTop: 16,
        background: theme.lightBlueGrey,
        border: `1px solid ${theme.grayLight}`,
        borderRadius: theme.borderRadius,
        overflow: 'hidden'
    },
    toggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '12px 16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: theme.fontFamily,
        fontSize: 13,
        fontWeight: 600,
        color: theme.textPrimary,
        textAlign: 'left',
        transition: 'background 140ms ease',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.03)'
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.primary}`,
            outlineOffset: -2
        }
    },
    toggleIcon: {
        color: theme.primary,
        fontSize: 18,
        flexShrink: 0
    },
    toggleLabel: {
        flex: 1
    },
    chevron: {
        color: theme.textSecondary,
        fontSize: 20,
        transition: 'transform 180ms ease',
        flexShrink: 0
    },
    chevronOpen: {
        transform: 'rotate(180deg)'
    },
    body: {
        padding: '4px 20px 18px',
        borderTop: `1px solid ${theme.grayLight}`,
        background: theme.background
    },
    title: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        marginTop: 14,
        marginBottom: 10,
        letterSpacing: 0.2
    },
    paragraph: {
        fontSize: 13,
        lineHeight: 1.6,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    stepsList: {
        listStyle: 'none',
        padding: 0,
        margin: '0 0 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    stepItem: {
        display: 'grid',
        gridTemplateColumns: '22px 1fr',
        gap: 10,
        alignItems: 'flex-start'
    },
    stepNumber: {
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: theme.tertiary,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        fontSize: 11,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 1
    },
    stepText: {
        fontSize: 13,
        lineHeight: 1.5,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        '& strong': {
            fontWeight: 700,
            color: theme.textPrimary
        }
    },
    support: {
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px dashed ${theme.grayLight}`,
        fontSize: 12,
        lineHeight: 1.5,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        fontStyle: 'italic'
    }
}))

function emphasize(text) {
    if (typeof text !== 'string') return text
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>
        }
        return <React.Fragment key={i}>{part}</React.Fragment>
    })
}

export default function IntegrationHint({
    toggleLabel,
    title,
    body,
    steps,
    support,
    defaultOpen = false
}) {
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(defaultOpen)

    return (
        <div className={classes.wrapper}>
            <button
                type="button"
                className={classes.toggle}
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
            >
                <HelpOutlineIcon className={classes.toggleIcon} />
                <span className={classes.toggleLabel}>{toggleLabel}</span>
                <ExpandMoreIcon
                    className={`${classes.chevron} ${
                        isOpen ? classes.chevronOpen : ''
                    }`}
                />
            </button>
            <Collapse in={isOpen} timeout={220}>
                <div className={classes.body}>
                    {title && (
                        <Typography className={classes.title}>
                            {title}
                        </Typography>
                    )}
                    {body && (
                        <Typography className={classes.paragraph}>
                            {emphasize(body)}
                        </Typography>
                    )}
                    {steps && Object.values(steps).length > 0 && (
                        <ol className={classes.stepsList}>
                            {Object.values(steps).map((step, idx) => (
                                <li key={idx} className={classes.stepItem}>
                                    <span className={classes.stepNumber}>
                                        {idx + 1}
                                    </span>
                                    <span className={classes.stepText}>
                                        {emphasize(step)}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    )}
                    {support && (
                        <div className={classes.support}>{support}</div>
                    )}
                </div>
            </Collapse>
        </div>
    )
}
